export class structureTreeTableService {
  /**
   * Empty Node item
   */
  static newNodeItem(data) {
    return {
      data,
      children: [],
    }
  }

  /**
   * Filters the treeData array to find all nodes where keyToSearch matches valueToSearch.
   *
   * @param {Array} treeData - The array of nodes (tree structure).
   * @param {string} keyToSearch - The key to search in the node's data.
   * @param {*} valueToSearch - The value to match against the key.
   * @returns {Array} - The filtered array of matching nodes.
   */
  static filterTreeData(treeData, keyToSearch, valueToSearch) {
    const result = []

    treeData.forEach((node) => {
      // Check if the current node's data matches
      if (
        node.data
        && node.data[keyToSearch]
        && node.data[keyToSearch]
          .toLowerCase()
          .includes(valueToSearch.toLowerCase())
      ) {
        result.push(node)
      }
      // Recursively search children if they exist
      if (node.children && node.children.length > 0) {
        result.push(
          ...this.filterTreeData(node.children, keyToSearch, valueToSearch),
        )
      }
    })

    return result
  }

  /**
   * Find a node in the tree by its key
   *
   * @param {Array} treeData - The array of nodes (tree structure)
   * @param {string} nodeId - The key of the node to find
   * @returns {object | null} - The node with the matching key, or null if not found
   */
  static findNodeById(treeData, nodeId) {
    for (const node of treeData) {
      // If the current node matches the parentId, return it
      if (node.key === nodeId) {
        return node
      }

      // If the node has children, recursively search them
      if (node.children && node.children.length > 0) {
        const foundNode = this.findNodeById(node.children, nodeId)
        if (foundNode) {
          return foundNode // Return the found node
        }
      }
    }

    return null // Return null if no matching node is found
  }

  /**
   * Finds the parent node key for a given node key in the tree.
   *
   * @param {Array} nodeTree - The array of nodes (tree structure).
   * @param {string} childKey - The key of the node whose parent's key is to be found.
   * @returns {string | null} - The key of the parent node, or null if no parent is found.
   */
  static findParentNode(nodeTree, childKey) {
    for (const node of nodeTree) {
      // Check if any child of the current node matches the childKey
      if (
        node.children
        && node.children.some(child => child.key === childKey)
      ) {
        return node // Return the  current node as the parent
      }

      // If the node has children, recursively search them
      if (node.children && node.children.length > 0) {
        const parentNode = this.findParentNode(node.children, childKey)
        if (parentNode) {
          return parentNode // Return the found parent's node
        }
      }
    }

    return null // Return null if no parent is found
  }

  /**
   * Finds the first parent key of a node in the tree by its key.
   *
   * @param {Array} treeData - The array of nodes (tree structure).
   * @param {string} nodeId - The key of the node whose parent key is to be found.
   * @returns {string | null} - The key of the parent node, or the node's own key if it's a top-level node, or null if not found.
   */
  static findFirstNodeParentKey(treeData, nodeId) {
    for (const node of treeData) {
      // If the current node matches the nodeId, return its key
      if (node.key === nodeId) {
        return node.key // The node itself is the top-level node
      }

      // Check if any child of the current node matches the nodeId
      if (
        node.children
        && node.children.some(child => child.key === nodeId)
      ) {
        return node.key // Return the key of the current node as the parent
      }

      // If the node has children, recursively search them
      if (node.children && node.children.length > 0) {
        const parentKey = this.findFirstNodeParentKey(node.children, nodeId)
        if (parentKey) {
          return parentKey // Return the found parent's key
        }
      }
    }

    return null // Return null if no parent is found
  }

  static findParentSiteKey(treeArray, childKey) {
    // Recursive helper function
    function traverse(node, ancestors) {
      // Check if current node is the one we are looking for.
      if (node.key === childKey) {
        // Look backwards in the ancestors array for a node with type "site"
        for (let index = ancestors.length - 1; index >= 0; index--) {
          if (ancestors[index].type === 'site') {
            return ancestors[index].key
          }
        }
        return null // No parent with type "site" found.
      }

      // If the node has children, traverse them.
      if (node.children && node.children.length > 0) {
        for (const child of node.children) {
          const result = traverse(child, ancestors.concat(node))
          if (result !== null) {
            return result
          }
        }
      }

      return null
    }

    // Since treeArray is an array of nodes, loop through each.
    for (const node of treeArray) {
      const result = traverse(node, [])
      if (result !== null) {
        return result
      }
    }

    return null
  }

  /**
   * Edit a node tree merging the new data with current data
   *
   * @param treeData -
   * @param key -
   * @param newData
   * @returns {boolean} - True if has node
   */
  static editNode(treeData, key, newData) {
    const node = this.findParentNodeById(treeData, key)
    if (node) {
      node.data = { ...node.data, ...newData } // Merge the existing data with new data
      return true
    }
    return false // Node not found
  }

  /**
   * Check if a node with the given key is a first-level node
   *
   * @param {Array} treeData - The root tree data
   * @param {string} key - The key of the node to check
   * @returns {boolean} - True if the node is a first-level node, false otherwise
   */
  static isFirstLevelNode(treeData, key) {
    return treeData.some(node => node.key === key)
  }

  /**
   * Updates the nodeTree by adding a full name for each responsible user based on the companyUsers list.
   *
   * @param {Array | object} nodeTree - The array or object representing the node tree structure.
   * @param {Array | object} companyUsers - The array or object containing company user information.
   * @returns {Array} - The updated nodeTree with full names for responsible users.
   */
  /**
   * Updates the nodeTree by adding or updating the full name for each responsible user
   * and appending the level information for all nodes recursively.
   *
   * @param {Array} nodeTree - The array of nodes (tree structure).
   * @param {Array} companyUsers - The array of company user objects.
   * @param {number} level - The current level in the tree (default: 1).
   * @returns {Array} - The updated nodeTree with full names and levels.
   */
  static updateNodeTreeWithResponsible(nodeTree, companyUsers, level = 1) {
    const updateNode = (nodes, currentLevel) => {
      return nodes.map((node) => {
        const updatedChildren = node.children?.length
          ? updateNode(node.children, currentLevel + 1)
          : []

        return {
          ...node,
          level: currentLevel, // Add the level information
          data: {
            ...node.data,
          },
          children: updatedChildren,
        }
      })
    }

    // Start the recursive update for all top-level nodes
    return updateNode(nodeTree, level)
  }

  /**
   * Add tooltips to each node based on the node level and a specified configuration.
   * Supports infinite repetition from a certain level onward.
   *
   * @param {Array} nodeTree - Result from updateNodeTreeWithResponsible()
   * @param {object} tooltipsConfig - Configuration object with keys for levels and possibly an `infiniteFrom` property.
   * @returns {Array} A new node tree with tooltip objects added
   */
  static addTooltipsToNodes(nodeTree, tooltipsConfig = {}, t) {
    const { infiniteFrom, ...levels } = tooltipsConfig

    const addTooltipsRec = (nodes) => {
      return nodes.map((node) => {
        // Determine which level config to use
        let config = levels[node.level]

        // If no direct config, but infiniteFrom is set and node.level >= infiniteFrom
        if (!config && infiniteFrom && node.level >= infiniteFrom) {
          config = levels[infiniteFrom]
        }

        // Specific for physical-structure type
        if (node.type === 'area') {
          config = levels.area
        }

        const tooltip = {
          add: config?.add ? t(config.add) : null,
          edit: config?.edit ? t(config.edit) : null,
          delete: config?.delete ? t(config.delete) : null,
        }

        const updatedChildren = node.children?.length
          ? addTooltipsRec(node.children)
          : []

        return {
          ...node,
          tooltip,
          children: updatedChildren,
        }
      })
    }

    return addTooltipsRec(nodeTree)
  }

  /**
   * Retrieve the node and its parent hierarchy based on the given key.
   *
   * @param {Array} nodeTree - The tree data array.
   * @param {string} nodeKey - The key of the node to find.
   * @returns {object} - The node and its parent hierarchy.
   */
  static getNodeHierarchy(nodeTree, nodeKey) {
    let result = null

    const findNode = (nodes, parents) => {
      for (const node of nodes) {
        if (node.key === nodeKey) {
          result = {
            node,
            hierarchy: [...parents, node.data.name],
          }
          return
        }

        if (node.children && node.children.length > 0) {
          findNode(node.children, [...parents, node.data.name])
        }
      }
    }

    findNode(nodeTree, [])
    return result
  }
}
